import React, { useEffect, useMemo, useRef } from 'react';

import {
  InternalLeafOption,
  InternalSubmenuOption,
  InternalSubmenuWrapper,
  InternalSubmenuHeader,
  InternalSubmenuGroupWrapper,
  InternalSubmenuGroupHeader,
  InternalSubmenuGroupItemList,
  InternalSubmenuGroupDivider,
} from '../subcomponents';
import type {
  CascaderOption,
  CascaderOptionGroup,
  CascaderSubmenu as CascaderSubmenuType,
} from '../types';
import { isSubmenu } from '../util';

interface CascaderSubmenuProps<OptionType extends CascaderOption> {
  submenu: CascaderSubmenuType<OptionType>;
  groups: CascaderOptionGroup<OptionType>[];
  level: number;
  onSelectOption: (option: OptionType, level: number) => void;
  cursorPath: number[];
  isOpen: boolean;
}

/**
 * CascaderSubmenu organizes and renders the individual options of a submenu, including
 * the submenu's header, and group dividers, and vertical scrolling for highlighted options.
 */
export const CascaderSubmenu = <OptionType extends CascaderOption>({
  submenu,
  groups,
  level,
  onSelectOption,
  cursorPath,
  isOpen,
}: CascaderSubmenuProps<OptionType>) => {
  const options = groups.flatMap((group) => group.children);
  const submenuGroupRef = useRef<HTMLDivElement>(null);
  const highlightedOptionRef = useRef<HTMLButtonElement>(null);

  // Determine which option should be highlighted based on cursorPath
  const highlightedIndex = useMemo(() => {
    const index = cursorPath[level];
    // If no index is set for this level, return null (no highlight)
    if (index === undefined || index === null) {
      return null;
    }
    // Ensure the index is within bounds of the options array
    if (index >= 0 && index < options.length) {
      return index;
    }
    // If the index is out of bounds, return null
    return null;
  }, [cursorPath, level, options.length]);

  const highlightedOption =
    highlightedIndex !== null ? options[highlightedIndex] : null;

  // Determine if this menu is the active menu
  // The active menu should be the one at the level where we're currently navigating
  // The cursor path represents selected options, so the current navigation level is cursorPath.length - 1
  const isActiveMenu = useMemo(() => {
    // If there's no cursor path, the first menu (level 0) is active
    if (cursorPath.length === 0) {
      return level === 0;
    }

    // The active menu is the one at the current navigation level
    // cursorPath.length represents how many levels have been selected
    // The current navigation level is cursorPath.length - 1 (0-indexed)
    const currentNavigationLevel = cursorPath.length - 1;
    return level === currentNavigationLevel;
  }, [cursorPath.length, level]);

  // Scroll to highlighted option when it changes
  useEffect(() => {
    if (highlightedOptionRef.current && isActiveMenu && isOpen) {
      // Use a small delay to ensure the DOM has updated
      const timeoutId = setTimeout(() => {
        if (highlightedOptionRef.current) {
          highlightedOptionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [highlightedIndex, isActiveMenu, isOpen]);

  // Only render if the submenu is open
  if (!isOpen) {
    return null;
  }

  return (
    <InternalSubmenuWrapper aria-label={`Cascader Menu Level ${level + 1}`}>
      {submenu?.label && <InternalSubmenuHeader submenu={submenu} />}
      <InternalSubmenuGroupWrapper submenuGroupRef={submenuGroupRef}>
        {groups.map((group, groupIndex) => (
          <React.Fragment
            key={group.children.map((opt) => opt.value).join('-')}
          >
            {group.label && <InternalSubmenuGroupHeader label={group.label} />}
            <InternalSubmenuGroupItemList>
              {group.children.map((option) => {
                const isHighlighted = Boolean(
                  highlightedOption && option.value === highlightedOption.value,
                );

                return !isSubmenu(option) ? (
                  <InternalLeafOption
                    key={option.value}
                    optionRef={isHighlighted ? highlightedOptionRef : undefined}
                    option={option}
                    onClick={(option) => onSelectOption(option, level)}
                    isHighlighted={isHighlighted}
                  />
                ) : (
                  <InternalSubmenuOption
                    key={option.value}
                    optionRef={isHighlighted ? highlightedOptionRef : undefined}
                    option={option}
                    onClick={(option) => onSelectOption(option, level)}
                    isHighlighted={isHighlighted}
                  />
                );
              })}
              {groupIndex < groups.length - 1 && (
                <InternalSubmenuGroupDivider />
              )}
            </InternalSubmenuGroupItemList>
          </React.Fragment>
        ))}
      </InternalSubmenuGroupWrapper>
    </InternalSubmenuWrapper>
  );
};
